import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';

export interface Membership {
  id: string;
  marcaTemporal: string;
  nombres: string;
  apellidos: string;
  fechaNacimiento: string;
  genero: string;
  estadoCivil: string;
  telefono: string;
  email: string;
  zonaBarrio: string;
  numeroDocumento?: string;
  tipoDocumento?: string;
  consentimientoDatos: boolean;
  autorizacionImagen: boolean;
  observacionesLegales?: string;
  creadoEn: string;
  actualizadoEn: string;
  estado: string;
}

export interface DatosComplementarios {
  id: string;
  membresiaId: string;
  nivelEstudios?: string;
  profesion?: string;
  tiempoAsistencia?: string;
  oracionFe: boolean;
  esBautizado: boolean;
  añoBautismo?: number;
  iglesiaBautismo?: string;
  ministroBautismo?: string;
  estudiosRealizados: string[];
  tipoPersonalidad?: string;
  habilidades: string[];
  dones: string[];
  perteneceMinisterio: boolean;
  ministeriosActuales: string[];
  ministerioDeseado?: string;
  fechaEncuesta: string;
  creadoEn: string;
  actualizadoEn: string;
}

export interface MinisterioMiembro {
  id: string;
  nombre: string;
  descripcion?: string;
  rol?: string;
  fechaUnion: string;
}

export interface MinisterioLider {
  id: string;
  nombre: string;
  descripcion?: string;
  mision?: string;
  vision?: string;
  estado: boolean;
}

export interface EventoInscripcion {
  id: string;
  titulo: string;
  descripcion?: string;
  fecha: string;
  horaInicio?: string;
  horaFin?: string;
  lugar?: string;
  estado: string;
  estadoInscripcion: string;
}

export interface MemberProfile {
  membresia: {
    id: string;
    nombres: string;
    apellidos: string;
    nombreCompleto: string;
    fechaNacimiento: string;
    genero: string;
    estadoCivil: string;
    telefono: string;
    email: string;
    zonaBarrio: string;
    numeroDocumento?: string;
    tipoDocumento?: string;
    estado: string;
    creadoEn: string;
  };
  datosComplementarios: DatosComplementarios | null;
  ministerios: MinisterioMiembro[];
  ministeriosLiderados: MinisterioLider[];
  eventos: EventoInscripcion[];
}

export interface CreateMembershipDto {
  nombres: string;
  apellidos: string;
  fechaNacimiento: string;
  genero: string;
  estadoCivil: string;
  telefono: string;
  email: string;
  zonaBarrio: string;
  numeroDocumento?: string;
  tipoDocumento?: string;
  consentimientoDatos: boolean;
  autorizacionImagen: boolean;
  observacionesLegales?: string;
}

class MembershipsServiceClass {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async createMembership(data: CreateMembershipDto): Promise<Membership> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.create}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al crear la membresía');
    }

    const result = await response.json();
    return result.data || result;
  }

  async createMembershipAdmin(data: CreateMembershipDto, creadoPorId: string): Promise<Membership> {
    const payload = { ...data, creadoPorId };
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.createAdmin}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al crear la membresía');
    }

    const result = await response.json();
    return result.data || result;
  }

  async countByRegistrador(ministerioId: string): Promise<Array<{ creadoPorId: string; nombre: string; count: number; miembros: Array<{ id: string; nombreCompleto: string; creadoEn: string }> }>> {
    const response = await fetch(`${API_BASE_URL}/memberships/created-by?ministerioId=${encodeURIComponent(ministerioId)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener conteo');
    }

    const result = await response.json();
    return result.data || result;
  }

  async getMembership(id: string): Promise<Membership> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.detail(id)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener la membresía');
    }

    const result = await response.json();
    return result.data || result;
  }

  async getMyProfile(): Promise<MemberProfile> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.me}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener el perfil del miembro');
    }

    const result = await response.json();
    return result.data || result;
  }

  async updateMembership(id: string, data: Partial<CreateMembershipDto>): Promise<Membership> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.update(id)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al actualizar la membresía');
    }

    const result = await response.json();
    return result.data || result;
  }

  async searchMemberships(search: string, page = 1, limit = 20): Promise<{ data: Membership[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', search);
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.list}?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al buscar membresías');
    }
    const result = await response.json();
    return result.data || result;
  }

  async deleteMembership(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.delete(id)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al eliminar la membresía');
    }
  }

  private cleanDatosComplementariosPayload(data: Partial<DatosComplementarios>): Partial<DatosComplementarios> {
    // Solo enviar los campos que el backend espera, eliminando metadata
    const allowedFields: Array<keyof DatosComplementarios> = [
      'nivelEstudios',
      'profesion',
      'tiempoAsistencia',
      'oracionFe',
      'esBautizado',
      'añoBautismo',
      'iglesiaBautismo',
      'ministroBautismo',
      'estudiosRealizados',
      'tipoPersonalidad',
      'habilidades',
      'dones',
      'perteneceMinisterio',
      'ministeriosActuales',
      'ministerioDeseado',
      'fechaEncuesta',
    ];

    const cleaned: Partial<DatosComplementarios> = {};
    for (const key of allowedFields) {
      if (key in data && data[key] !== undefined) {
        (cleaned as any)[key] = data[key];
      }
    }
    return cleaned;
  }

  async getDatosComplementarios(membresiaId: string): Promise<DatosComplementarios | null> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.datosComplementarios(membresiaId)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 404) {
      return null; // No hay datos complementarios aún
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener los datos complementarios');
    }

    const result = await response.json();
    // El backend envuelve respuestas en { data, statusCode, timestamp }
    if (result && 'data' in result && 'statusCode' in result) {
      return result.data as DatosComplementarios | null;
    }
    return result as DatosComplementarios | null;
  }

  async createDatosComplementarios(
    membresiaId: string, 
    data: Partial<DatosComplementarios>
  ): Promise<DatosComplementarios> {
    const cleanedData = this.cleanDatosComplementariosPayload(data);
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.datosComplementarios(membresiaId)}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(cleanedData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al crear los datos complementarios');
    }

    const result = await response.json();
    return result.data || result;
  }

  async updateDatosComplementarios(
    membresiaId: string, 
    data: Partial<DatosComplementarios>
  ): Promise<DatosComplementarios> {
    const cleanedData = this.cleanDatosComplementariosPayload(data);
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.datosComplementarios(membresiaId)}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(cleanedData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al actualizar los datos complementarios');
    }

    const result = await response.json();
    return result.data || result;
  }
}

export const MembershipsService = new MembershipsServiceClass();